import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import '../styles.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotForm, setShowForgotForm] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [showResendButton, setShowResendButton] = useState(false); // New state for resend button
  const [resendLoading, setResendLoading] = useState(false); // New state for resend loading
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setShowResendButton(false); // Reset resend button visibility
    setLoading(true);
    console.log('Login attempt:', { email, password });

    try {
      // Clean up API_URL to remove trailing slash
      const apiUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      const res = await axios.post(`${apiUrl}/api/auth/login`, { email, password }, {
        headers: { 'Content-Type': 'application/json' },
      });
      console.log('Login response:', res.data);
      const { token } = res.data;

      if (token) {
        localStorage.setItem('token', token);
        const decodedToken = jwtDecode(token);
        const isAdmin = decodedToken.is_admin === 1;

        setSuccessMessage('Login successful! Redirecting...');
        setTimeout(() => {
          navigate(isAdmin ? '/adminDashboard' : '/presale');
        }, 500);
      } else {
        throw new Error('No token received');
      }
    } catch (err) {
      console.error('Login error:', err.response?.data || err.message);
      const status = err.response?.status;
      let errorMessage = err.response?.data?.error || 'Login failed';

      if (status === 401) {
        errorMessage = 'Invalid email or password';
      } else if (status === 403) {
        // User is registered but not verified
        errorMessage = "Please verify your email first. If you don't receive the verification link, please click 'Resend Verification'.";
        setShowResendButton(true); // Show the resend button
      } else if (status === 500) {
        errorMessage = 'Server error, please try again later';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setError('');
    setSuccessMessage('');
    setResendLoading(true);

    try {
      const apiUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      const resendRes = await axios.post(`${apiUrl}/api/auth/resend-verification`, { email }, {
        headers: { 'Content-Type': 'application/json' },
      });
      console.log('Resend verification response:', resendRes.data);
      setSuccessMessage('A new verification link has been sent to your email. Please check your inbox (and spam/junk folder) to verify your email.');
    } catch (resendErr) {
      console.error('Resend verification error:', resendErr.response?.data || resendErr.message);
      setError('Failed to resend verification link: ' + (resendErr.response?.data?.error || 'Server error'));
    } finally {
      setResendLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setForgotSuccess('');
    setForgotLoading(true);

    try {
      // Clean up API_URL to remove trailing slash
      const apiUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
      const res = await axios.post(`${apiUrl}/api/auth/forgot-password`, { email: forgotEmail });
      console.log('Forgot password response:', res.data);
      setForgotSuccess('A reset link has been sent to your email.');
    } catch (err) {
      console.error('Forgot password error:', err.response?.data || err.message);
      const status = err.response?.status;
      let errorMessage = err.response?.data?.error || 'Failed to send reset link';
      if (status === 404) errorMessage = 'Email not found';
      else if (status === 500) errorMessage = 'Server error, please try again later';
      setError(errorMessage);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleDismissForgot = () => {
    setForgotSuccess('');
    setShowForgotForm(false);
    setForgotEmail('');
  };

  return (
    <div className="page-container auth-page">
      <section className="content-container container my-4">
        <div className="login-card p-4 mx-auto card-max-width">
          <h2 className="text-center mb-4">{showForgotForm ? 'Forgot Password' : 'Login'}</h2>
          {!showForgotForm ? (
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  required
                  disabled={loading}
                />
              </div>
              <div className="mb-3">
                <input
                  type="password"
                  className="form-control"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  disabled={loading}
                />
              </div>
              {error && <p className="text-danger text-center">{error}</p>}
              {successMessage && (
                <div className="text-success text-center mb-3">
                  <p>{successMessage}</p>
                </div>
              )}
              {showResendButton && (
                <div className="text-center mb-3">
                  <button
                    type="button"
                    className="btn btn-link"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                  >
                    {resendLoading ? 'Resending...' : 'Resend Verification'}
                  </button>
                </div>
              )}
              <button type="submit" className="btn btn-primary w-100" disabled={loading}>
                {loading ? 'Logging in...' : 'Login'}
              </button>
              <p className="text-center mt-3">
                Don’t have an account? <Link to="/register" className="text-primary">Register here</Link>
              </p>
              <p className="text-center mt-2">
                <button type="button" className="btn btn-link p-0" onClick={() => setShowForgotForm(true)}>
                  Forgot Password?
                </button>
              </p>
            </form>
          ) : (
            <form onSubmit={handleForgotPassword}>
              <div className="mb-3">
                <input
                  type="email"
                  className="form-control"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={forgotLoading}
                />
              </div>
              {error && <p className="text-danger text-center">{error}</p>}
              {forgotSuccess && (
                <div className="text-success text-center mb-3">
                  <p>{forgotSuccess}</p>
                  <button type="button" className="btn btn-success mt-2" onClick={handleDismissForgot}>
                    OK
                  </button>
                </div>
              )}
              {!forgotSuccess && (
                <button type="submit" className="btn btn-primary w-100" disabled={forgotLoading}>
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary w-100 mt-2"
                onClick={() => setShowForgotForm(false)}
                disabled={forgotLoading}
              >
                Cancel
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default Login;
